/**
 * Mojo Up AI Cloud sign-in: a button for each provider Cloud offers (Google, Microsoft, GitHub,
 * Facebook, in that order), then "Continue with email" — the address, then the six digits sent to
 * it. Nothing here holds a password; Cloud only learns who the person is and issues its own tokens.
 */

import type { SignInProvider } from '@aioe/shared';
import { Button, InputOTP, Input, Label, Separator, TextField } from '@heroui/react';
import { type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { routes } from '../../api/client.ts';
import { startEmailConfirm, startEmailSignIn, startProviderSignIn, verifyEmailCode } from '../../auth/cloudSession.ts';
import { useLoad } from '../../hooks.ts';
import { ErrorNote } from '../ui.tsx';
import { BrandMark } from './BrandMark.tsx';
import { Turnstile, useTurnstileSiteKey } from './Turnstile.tsx';

type Provider = Exclude<SignInProvider, 'email'>;
const ORDER: Provider[] = ['google', 'microsoft', 'github', 'facebook'];
const LABEL: Record<Provider, string> = { google: 'Google', microsoft: 'Microsoft', github: 'GitHub', facebook: 'Facebook' };

/** `returnTo`: where the person goes once signed in (a path on this console). */
export function SignInPanel({ returnTo }: { returnTo: string }) {
  const navigate = useNavigate();
  const offered = useLoad(() => routes.signInProviders(), []);
  const providers = ORDER.filter((p) => offered.data?.providers.some((o) => o.id === p));
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string>();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  // Sign-up checks: when Cloud asks, the email form shows the person check and sends its answer.
  const siteKey = useTurnstileSiteKey();
  const [check, setCheck] = useState<string>();
  const [checkRound, setCheckRound] = useState(0);

  const run = async (what: string, f: () => Promise<void>) => {
    setBusy(what);
    setError(undefined);
    try {
      await f();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(undefined);
    }
  };
  const address = email.trim().toLowerCase();
  const sendCode = () =>
    run('email', async () => {
      try {
        await startEmailSignIn(address, returnTo, undefined, check);
      } finally {
        // An answer is spent once: a fresh check for the next try.
        setCheck(undefined);
        setCheckRound((n) => n + 1);
      }
      setSentTo(address);
      setCode('');
    });
  const canSend = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address) && (!siteKey || Boolean(check));
  const verify = (digits = code) =>
    run('verify', async () => {
      try {
        const next = await verifyEmailCode(sentTo!, digits);
        navigate(next, { replace: true });
      } catch (e) {
        // A wrong code is cleared, so the next try starts from the first box.
        setCode('');
        throw e;
      }
    });

  if (sentTo) {
    return (
      <CheckEmail
        sentTo={sentTo}
        code={code}
        onCode={(v) => {
          setCode(v);
          setError(undefined);
        }}
        onVerify={(v) => void verify(v)}
        error={error}
        busy={busy}
        submitLabel="Sign in"
        onChangeAddress={() => {
          setSentTo(undefined);
          setError(undefined);
        }}
        onResend={() => (siteKey ? setSentTo(undefined) : void sendCode())}
      />
    );
  }

  return (
    <div className="grid gap-4">
      {providers.length > 0 && (
        <>
          <div className="grid gap-2">
            {providers.map((p) => (
              <Button key={p} variant="tertiary" fullWidth isPending={busy === p} isDisabled={Boolean(busy) && busy !== p} onPress={() => void run(p, () => startProviderSignIn(p, returnTo))}>
                <BrandMark provider={p} />
                Continue with {LABEL[p]}
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-3 text-xs text-muted" role="separator" aria-label="or">
            <Separator className="flex-1" />
            or
            <Separator className="flex-1" />
          </div>
        </>
      )}
      <form
        className="grid gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSend) void sendCode();
        }}
      >
        <TextField variant="secondary" type="email" value={email} onChange={setEmail}>
          <Label>Email</Label>
          <Input placeholder="you@example.com" autoComplete="email" />
        </TextField>
        {siteKey ? <Turnstile key={checkRound} siteKey={siteKey} onToken={setCheck} /> : null}
        <ErrorNote error={error} />
        <Button type="submit" variant="primary" fullWidth isPending={busy === 'email'} isDisabled={!canSend || (Boolean(busy) && busy !== 'email')}>
          Continue with email
        </Button>
      </form>
    </div>
  );
}

/** A quiet text action: an escape hatch beside the main one. */
/** The six digits sent to `sentTo`: the boxes, the submit, and the ways back (another address, a new code). */
function CheckEmail(props: { sentTo: string; code: string; onCode: (v: string) => void; onVerify: (digits?: string) => void; error?: string; busy?: string; submitLabel: string; intro?: ReactNode; onChangeAddress: () => void; onResend: () => void }) {
  const { sentTo, code, error, busy } = props;
  return (
    <div className="grid gap-4">
      <div className="grid gap-1 text-center">
        <h2 className="text-base font-semibold">Check your email</h2>
        <p className="text-sm text-muted">
          {props.intro ?? 'We sent a six-digit code to '}
          <span className="font-medium text-foreground">{sentTo}</span>. It works for ten minutes.
        </p>
      </div>
      <form
        className="grid justify-items-center gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (code.length === 6) props.onVerify();
        }}
      >
        {/* The group is a full-width row that starts at the left; centre its boxes under the heading. */}
        <InputOTP
          className="justify-center"
          aria-label="Six-digit code"
          maxLength={6}
          value={code}
          onChange={(v) => props.onCode(v.replace(/\D/g, ''))}
          onComplete={(v: string) => props.onVerify(v)}
          pattern="^\d+$"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          isInvalid={Boolean(error)}
          isDisabled={busy === 'verify'}
        >
          <InputOTP.Group>
            {[0, 1, 2].map((i) => (
              <InputOTP.Slot key={i} index={i} />
            ))}
          </InputOTP.Group>
          <InputOTP.Separator />
          <InputOTP.Group>
            {[3, 4, 5].map((i) => (
              <InputOTP.Slot key={i} index={i} />
            ))}
          </InputOTP.Group>
        </InputOTP>
        <ErrorNote error={error} />
        <Button type="submit" variant="primary" fullWidth isPending={busy === 'verify'} isDisabled={code.length !== 6}>
          {props.submitLabel}
        </Button>
      </form>
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm">
        <LinkButton onPress={props.onChangeAddress}>Use a different email</LinkButton>
        <LinkButton onPress={props.onResend} isDisabled={Boolean(busy)}>
          {busy === 'email' ? 'Sending…' : 'Send a new code'}
        </LinkButton>
      </div>
    </div>
  );
}

/**
 * A provider let the person in without vouching for their address (Facebook never does, a work Microsoft account
 * often does not). Before the sign-in finishes they prove an address with a code: the account then joins whoever
 * already signs in with that address, so one person keeps one account however they sign in.
 */
export function ConfirmEmailPanel({ suggested, provider, onCancel }: { suggested?: string; provider: string; onCancel: () => void }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState(suggested ?? '');
  const [sentTo, setSentTo] = useState<string>();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  const address = email.trim().toLowerCase();
  const run = async (what: string, f: () => Promise<void>) => {
    setBusy(what);
    setError(undefined);
    try {
      await f();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(undefined);
    }
  };
  const sendCode = () =>
    run('email', async () => {
      await startEmailConfirm(address);
      setSentTo(address);
      setCode('');
    });
  const verify = (digits = code) =>
    run('verify', async () => {
      try {
        navigate(await verifyEmailCode(sentTo!, digits), { replace: true });
      } catch (e) {
        setCode('');
        throw e;
      }
    });
  if (sentTo) {
    return (
      <CheckEmail
        sentTo={sentTo}
        code={code}
        onCode={(v) => {
          setCode(v);
          setError(undefined);
        }}
        onVerify={(v) => void verify(v)}
        error={error}
        busy={busy}
        submitLabel="Confirm and sign in"
        onChangeAddress={() => {
          setSentTo(undefined);
          setError(undefined);
        }}
        onResend={() => void sendCode()}
      />
    );
  }
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) void sendCode();
      }}
    >
      <p className="text-sm text-muted">
        {provider} signed you in but did not confirm your email address. We will send it a code once, so this sign-in joins your AI Cloud account if you already have one.
      </p>
      <TextField variant="secondary" type="email" value={email} onChange={setEmail}>
        <Label>Email</Label>
        <Input placeholder="you@example.com" autoComplete="email" autoFocus={!suggested} />
      </TextField>
      <ErrorNote error={error} />
      <Button type="submit" variant="primary" fullWidth isPending={busy === 'email'} isDisabled={!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)}>
        Send code
      </Button>
      <div className="flex justify-center text-sm">
        <LinkButton onPress={onCancel}>Choose another way to sign in</LinkButton>
      </div>
    </form>
  );
}

export function LinkButton({ children, onPress, isDisabled }: { children: ReactNode; onPress: () => void; isDisabled?: boolean }) {
  return (
    <button type="button" className="cursor-[var(--cursor-interactive)] text-accent hover:underline disabled:cursor-default disabled:opacity-50 disabled:hover:no-underline" disabled={isDisabled} onClick={onPress}>
      {children}
    </button>
  );
}

/** The page every signed-out view stands on: the Mojo Up mark lit from behind, a title, and a card. */
export function AuthFrame({ title, blurb, children, footer }: { title: ReactNode; blurb?: ReactNode; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="auth-frame app-content flex min-h-full flex-col items-center justify-center px-4 py-10 sm:py-16">
      <div className="art-hero" data-tone="teal">
        <span className="art-hero__glow" aria-hidden="true" />
        <img src="/mojoup-logo.svg" alt="Mojo Up" className="mb-2 size-12 rounded-xl object-contain" draggable={false} />
        <h1 className="art-hero__title">{title}</h1>
        {blurb ? <p className="art-hero__blurb">{blurb}</p> : null}
      </div>
      <div className="glass-card mt-6 w-full max-w-sm rounded-2xl p-6">{children}</div>
      {footer ? <div className="mt-6 max-w-sm text-center text-xs text-muted">{footer}</div> : null}
    </div>
  );
}
